import { Fragment } from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  label?: string;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  className?: string;
}

export function Select({ label, options, value, onChange, placeholder, error, className }: SelectProps) {
  const selected = options.find((o) => o.value === value);

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
      )}
      <Listbox value={value} onChange={onChange}>
        <div className="relative">
          <ListboxButton
            className={cn(
              'relative w-full cursor-pointer rounded-lg border bg-white py-2 pl-3 pr-10 text-left text-sm focus:outline-none focus:ring-1 dark:bg-gray-800 dark:text-gray-100',
              error
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:border-brand-500 focus:ring-brand-500 dark:border-gray-600',
            )}
          >
            <span className={cn(!selected && 'text-gray-400 dark:text-gray-500')}>
              {selected?.label ?? placeholder ?? '-'}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
            </span>
          </ListboxButton>
          <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <ListboxOptions className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none dark:border-gray-700 dark:bg-gray-800">
              {options.map((opt) => (
                <ListboxOption
                  key={opt.value}
                  value={opt.value}
                  className={({ focus }) =>
                    cn('cursor-pointer select-none px-3 py-2', focus && 'bg-brand-50 dark:bg-brand-900/20')
                  }
                >
                  {({ selected: isSelected }) => (
                    <div className="flex items-center justify-between">
                      <span className={cn(isSelected ? 'font-medium text-brand-600 dark:text-brand-400' : 'text-gray-700 dark:text-gray-200')}>
                        {opt.label}
                      </span>
                      {isSelected && <CheckIcon className="h-4 w-4 text-brand-600 dark:text-brand-400" />}
                    </div>
                  )}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Transition>
        </div>
      </Listbox>
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
}
